import { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { ReportFilterType } from "../../models/enumerations/report-filter-type";
import { ReportFilterDefinition } from "../../models/interfaces/report-filter-definition";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import { Button } from "../button/button";

const COMPONENT_CLASS = "c-report-filter-menu";

export interface ReportFilterMenuProps {
    filters: ReportFilterDefinition[];
    values: Record<string, any>;
    onChange: (key: string, value: any) => void;

    children?: React.ReactNode;
}

const ReportFilterMenu: React.FC<ReportFilterMenuProps> = ({
    filters,
    values,
    onChange,
    children
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [openDirection, setOpenDirection] = useState<"left" | "right">("right");
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!containerRef.current) return;

            if (!containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const renderFilter = (filter: ReportFilterDefinition) => {
        switch (filter.type) {
            case ReportFilterType.MultiSelect:
                return (
                    <Select
                        isMulti
                        placeholder={filter.placeholder ?? filter.label}
                        options={filter.options}
                        value={filter.options?.filter(o =>
                            values[filter.key]?.includes(o.value)
                        )}
                        onChange={(selected: any) =>
                            onChange(
                                filter.key,
                                selected ? selected.map((s: any) => s.value) : []
                            )
                        }
                    />
                );

            case ReportFilterType.SingleSelect:
                return (
                    <Select
                        placeholder={filter.placeholder ?? filter.label}
                        options={filter.options}
                        value={filter.options?.find(o =>
                            o.value === values[filter.key]
                        )}
                        onChange={(selected: any) =>
                            onChange(filter.key, selected?.value ?? null)
                        }
                    />
                );

            case ReportFilterType.Text:
                return (
                    <input
                        className="input-field"
                        placeholder={filter.placeholder ?? filter.label}
                        value={values[filter.key] ?? ""}
                        onChange={(e) => onChange(filter.key, e.target.value)}
                    />
                );

            default:
                return null;
        }
    };

    return (
        <div ref={containerRef} className={COMPONENT_CLASS}>
            <Button
                buttonText="Filter"
                buttonStyle="secondary"
                customIcon={<FilterListRoundedIcon fontSize="small" />}
                onClick={() => {
                    if (!containerRef.current) return;

                    const rect = containerRef.current.getBoundingClientRect();
                    const panelWidth = 320;

                    const spaceRight = window.innerWidth - rect.left;
                    const spaceLeft = rect.right;

                    if (spaceRight < panelWidth && spaceLeft > panelWidth) {
                        setOpenDirection("left");
                    } else {
                        setOpenDirection("right");
                    }

                    setIsOpen(prev => !prev);
                }}
            />

            {isOpen && (
                <div className={`${COMPONENT_CLASS}__panel ${COMPONENT_CLASS}__panel--${openDirection}`}>
                    {/* <div className={`${COMPONENT_CLASS}__header`}>
                        <div className={`${COMPONENT_CLASS}__icon bg-black-gray text-white`}>
                            <FilterListRoundedIcon fontSize="small" />
                        </div>
                        <h3>Filter</h3>
                    </div> */}

                    {children && (
                        <div className={`${COMPONENT_CLASS}__item`}>
                            {children}
                        </div>
                    )}

                    {filters.map(filter => (
                        <div key={filter.key} className={`${COMPONENT_CLASS}__item`}>
                            <label className={`${COMPONENT_CLASS}__label body-bold`}>
                                {filter.label}
                            </label>

                            {renderFilter(filter)}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ReportFilterMenu;